import React, { useRef, useEffect, useLayoutEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import IconProvider from '@/components/ui/IconProvider';
import FolderStructure, { FolderItem } from './FolderStructure';
import { FolderStructureType } from '@/contexts/FolderStructureVisibilityContext';
import { cn } from '@/lib/utils';
import ContextMenu, { ContextMenuItem } from '@/components/ui/ContextMenu';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { 
  getFolderTypeConfig, 
  getItemTypeRoute, 
  getItemTypeIcon, 
  getItemTypeLabel 
} from '@/lib/config/pageItemConfig';

interface CollapsibleFolderStructureProps {
  folderType: FolderStructureType;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  
  // FolderStructure props
  folders: FolderItem[];
  selectedFolderId?: string | null;
  onFolderSelect: (folderId: string | null) => void;
  onFolderCreate?: (parentId?: string) => void;
  onFolderRename?: (folderId: string, newName: string) => void;
  onFolderDelete?: (folderId: string) => void;
  onFolderColorChange?: (folderId: string, color: string | null) => void;
  onItemDrop?: (itemId: string, folderId: string | null) => void;
  onFolderMove?: (folderId: string, newParentId: string | null) => void;
  showItemCounts?: boolean;
  allowCreation?: boolean;
  allowRename?: boolean;
  allowDelete?: boolean;
  enableDragDrop?: boolean;
  chevronSize?: number;
  showFolderIcon?: boolean;
  folderIconSize?: number;
  isLoading?: boolean;
  
  // LinkEngine integration props
  gameId?: string;
  itemType?: 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable';
  
  // Item handling props
  showItems?: boolean;
  onItemSelect?: (item: any) => void;
  selectedItemId?: string | null;
  
  // Item creation props
  oncreateObject?: (folderId?: string) => void;
  onCreateDocument?: (folderId?: string) => void;
  onCreateBlueprint?: (folderId?: string) => void;
  onCreateDialog?: (folderId?: string) => void;
  onCreateFlow?: (folderId?: string) => void;
  onCreateVariable?: (folderId?: string) => void;
  
  // Item actions
  onItemRename?: (itemId: string, newName: string) => void;
  onItemDelete?: (itemId: string) => void;
  onItemDuplicate?: (itemId: string) => void;
  
  // Auto-edit props
  newlyCreatedFolderId?: string | null;
  onClearNewlyCreatedFolder?: () => void;
  
  // Total item count for header badge
  totalItemCount?: number;
}

export default function CollapsibleFolderStructure({
  folderType,
  isExpanded,
  onToggleExpanded,
  totalItemCount = 0,
  ...folderStructureProps
}: CollapsibleFolderStructureProps) {
  const { t } = usePlatformTranslation();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | 'auto'>(() => isExpanded ? 'auto' : 0);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  const isInitialMount = useRef(true);
  const previousExpandedState = useRef(isExpanded);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasBeenExpanded, setHasBeenExpanded] = useState(isExpanded); // Track if ever expanded
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
  }>({
    isOpen: false,
    position: { x: 0, y: 0 },
  });
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  
  // Get config from centralized location
  const config = getFolderTypeConfig(folderType);

  const getItemTypeForFolderType = (): NonNullable<CollapsibleFolderStructureProps['itemType']> => {
    if (folderStructureProps.itemType) {
      return folderStructureProps.itemType;
    }
    // Use centralized config
    return config.itemType;
  };
  
  // Mark as expanded when it opens for the first time
  useEffect(() => {
    if (isExpanded && !hasBeenExpanded) {
      setHasBeenExpanded(true);
    }
  }, [isExpanded, hasBeenExpanded]);

  // Handle add button click
  const handleAddButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get button position for context menu
    const rect = addButtonRef.current?.getBoundingClientRect();
    if (rect) {
      setContextMenu({
        isOpen: true,
        position: { x: rect.left, y: rect.bottom + 4 },
      });
    }
  };

  // Close context menu
  const closeContextMenu = () => {
    setContextMenu({ isOpen: false, position: { x: 0, y: 0 } });
  };

  const handleTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    const itemType = getItemTypeForFolderType();
    const path = getItemTypeRoute(itemType);

    // Navigate to the path - useAutoTabs will handle tab creation
    navigate(path);
  };

  // Handle left icon/chevron button click - expands/collapses
  const handleLeftIconButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpanded();
  };

  // Handle right icon button click - opens page in new tab
  const handleOpenInTabClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleTitleClick(e);
  };

  // Get context menu items based on folder type
  const getContextMenuItems = (): ContextMenuItem[] => {
    const items: ContextMenuItem[] = [];

    // Add folder creation option
    if (folderStructureProps.allowCreation && folderStructureProps.onFolderCreate) {
      items.push({
        id: 'create-folder',
        label: 'Create Folder',
        icon: 'fa-plus',
        onClick: () => {
          folderStructureProps.onFolderCreate?.();
          closeContextMenu();
        },
      });
    }

    // Add item creation options based on itemType
    if (folderStructureProps.allowCreation && folderStructureProps.itemType) {
      const itemType = folderStructureProps.itemType;
      const icon = getItemTypeIcon(itemType);
      const label = `Create ${getItemTypeLabel(itemType)}`;
      
      // Map item type to creation handler
      const creationHandlers: Record<string, (() => void) | undefined> = {
        'glossary': folderStructureProps.oncreateObject,
        'blueprint': folderStructureProps.onCreateBlueprint,
        'document': folderStructureProps.onCreateDocument,
        'dialog': folderStructureProps.onCreateDialog,
        'flow': folderStructureProps.onCreateFlow,
        'variable': folderStructureProps.onCreateVariable,
      };
      
      const handler = creationHandlers[itemType];
      if (handler) {
        items.push({
          id: `create-${itemType}`,
          label,
          icon,
          onClick: () => {
            handler();
            closeContextMenu();
          },
        });
      }
    }

    return items;
  };

  // Calculate content height for smooth animation
  // Using useLayoutEffect to prevent visual flicker
  useLayoutEffect(() => {
    // Skip animation on initial mount - just set the height immediately
    if (isInitialMount.current) {
      isInitialMount.current = false;
      previousExpandedState.current = isExpanded;
      return; // Height is already set in useState initializer
    }

    // Only animate if the expanded state actually changed
    if (previousExpandedState.current === isExpanded) {
      return;
    }
    
    previousExpandedState.current = isExpanded;

    if (!contentRef.current) return;
    
    setIsAnimating(true);
    
    if (isExpanded) {
      // Expanding: measure current height, then animate to full height
      const currentHeight = contentRef.current.scrollHeight;
      setContentHeight(currentHeight);
      
      // After animation completes, set to auto for dynamic content
      const timer = setTimeout(() => {
        setContentHeight('auto');
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      // Collapsing: set explicit height first, then animate to 0
      const currentHeight = contentRef.current.scrollHeight;
      setContentHeight(currentHeight);
      
      // Use RAF to ensure the explicit height is rendered before animating to 0
      requestAnimationFrame(() => {
        setContentHeight(0);
        // Mark animation as complete after transition
        setTimeout(() => {
          setIsAnimating(false);
        }, 300);
      });
    }
  }, [isExpanded]);

  return (
    <div className="w-full bg-sidebar border-b border-sidebar-border">
      {/* Header */}
      <div
        className={cn(
          'flex items-center justify-between px-3 py-2.5',
          'select-none'
        )}
        onMouseEnter={() => setIsHeaderHovered(true)}
        onMouseLeave={() => setIsHeaderHovered(false)}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Left Icon/Chevron button - shows icon by default, chevron on hover */}
          <button 
            className="flex-shrink-0 p-1 hover:bg-sidebar-accent/50 rounded cursor-pointer transition-all duration-200"
            onClick={handleLeftIconButtonClick}
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            <IconProvider
              iconstring={isHeaderHovered ? (isExpanded ? 'fa-chevron-down' : 'fa-chevron-right') : config.icon}
              size={isHeaderHovered ? 12 : 16}
              className={cn(
                'transition-all duration-200',
                isHeaderHovered ? 'text-sidebar-foreground/70' : 'text-sidebar-foreground'
              )}
            />
          </button>
          
          {/* Title - clickable to expand/collapse */}
          <span 
            className="text-sm font-semibold text-sidebar-foreground truncate cursor-pointer"
            title={config.label}
            onClick={handleLeftIconButtonClick}
          >
            {config.label}
          </span>
        </div>
        
        {/* Add button */}
        {folderStructureProps.allowCreation && (
          <button
            ref={addButtonRef}
            onClick={handleAddButtonClick}
            className="flex-shrink-0 p-1 hover:bg-sidebar-accent rounded text-sidebar-accent-foreground flex items-center justify-center"
            title={t('platform.layout.side-bars.add_folder_or_item')}
          >
            <IconProvider iconstring="fa-plus" size={14} />
          </button>
        )}
        
        {/* Right Icon - Opens page in new tab */}
        <button 
          className="flex-shrink-0 ml-2 p-1 hover:bg-sidebar-accent/50 rounded cursor-pointer transition-colors duration-150"
          onClick={handleOpenInTabClick}
          title="Open in tab"
        >
          <IconProvider
            iconstring="fa-arrow-right"
            style="auto"
            size={14}
            className="text-sidebar-foreground/70"
          />
        </button>
      </div>
      
      {/* Content */}
      <div
        ref={contentRef}
        style={{
          height: contentHeight === 'auto' ? 'auto' : `${contentHeight}px`,
          overflow: 'hidden', // Always hidden to prevent content showing during collapse
          willChange: isAnimating ? 'height' : 'auto', // Optimize animation performance
          pointerEvents: isAnimating ? 'none' : 'auto', // Prevent interactions during animation
        }}
        className="transition-all duration-300 ease-in-out"
      >
        {hasBeenExpanded && (
          <div className="pb-2">
            <FolderStructure
              {...folderStructureProps}
              title="" // We show the title in the header
              className="pt-0"
            />
          </div>
        )}
      </div>
      
      {/* Context Menu for + button */}
      <ContextMenu
        isOpen={contextMenu.isOpen}
        position={contextMenu.position}
        items={getContextMenuItems()}
        onClose={closeContextMenu}
      />
    </div>
  );
}
